<template>
  <v-container fluid class="py-4 px-4">
    <!-- Toolbar -->
    <v-app-bar app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <!-- Return to Home Button -->
      <v-btn icon @click="goToHome" class="back-btn">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-toolbar-title>
        <v-icon icon="mdi-folder"></v-icon> &nbsp; Projects List
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-menu>
  <template v-slot:activator="{ props }">
    <v-btn
      v-if="isAdminOrProjectLeader"
      color="white"
      class="toolbar-btn"
      v-bind="props"
    >
    <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
    &nbsp; Manage Selected
    </v-btn>
  </template>
  <v-list>
    <v-list-item
      :disabled="selectedProjects.length !== 1"
      @click="handleEdit"
    >
      <v-list-item-title>Edit</v-list-item-title>
    </v-list-item>
    <v-list-item
      :disabled="selectedProjects.length === 0"
      @click="handleDelete"
    >
      <v-list-item-title>Delete</v-list-item-title>
    </v-list-item>
  </v-list>
</v-menu>

      <!-- Create New Project Button -->
      <v-btn v-if="isAdminOrProjectLeader" color="white" class="toolbar-btn" @click="openCreateDialog">
        <v-icon>mdi-file-plus</v-icon>
        &nbsp; Create New Project
      </v-btn>
    </v-app-bar>

    <!-- Loading Spinner -->
    <v-row justify="center" align="center" v-if="loading" class="my-8">
      <v-progress-circular indeterminate color="primary" size="70"></v-progress-circular>
    </v-row>

    <!-- Projects Table -->
    <v-card flat >
      <div v-if="projects.length > 0">
      <v-card-title class="d-flex align-center pe-2">
        <!-- Search Field -->
        <v-text-field
          v-model="search"
          density="compact"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table v-if="!loading && projects.length !== 0"
        v-model:search="search"
        :headers="headers"
        :items="projects"
        item-value="id"
        class="elevation-2"
        dense
        hide-default-footer
      >

      <template v-slot:header.select>
    <v-checkbox
      :indeterminate="isSomeSelected"
      :input-value="isAllSelected"
      @click.stop="toggleSelectAll"
      hide-details
      density="compact"
    />
  </template>
        <!-- Custom Body for Actions -->
        <template v-slot:body="{ items }">
          <tr
            v-for="project in items"
            :key="project.id"
            @click="goToProject(project.id)"
            class="clickable-row"
          >
          <td>
            <v-checkbox
  v-model="selectedProjects"
  :value="project.id"
  hide-details
  density="compact"
  @click.stop
></v-checkbox>
    </td>
            <td>{{ project.name }}</td>
            <td>{{ project.description || "No description provided." }}</td>
            <!-- Owner Column -->
            <td>
              <div class="d-flex align-center">
                <v-avatar size="32" class="me-2">
                  <img :src="getImageUrl(project.creator.image)" alt="Owner's image" class="circle-image"/>
                </v-avatar>
                <span>{{ project.creator.username }}</span>
              </div>
            </td>
            <!-- Collaborators Column -->
            <td>
              <v-chip
                v-if="project.collaborators.length > 0"
                class="collaborators-chip"
                @click.stop="openCollaboratorsOverlay(project)"
                rounded
                size="small"
                :color="isDark ? 'white' : 'primary'"
                variant="outlined"
              >
                <div class="d-flex align-center">
                  <v-avatar
                    v-for="(collaborator, index) in project.collaborators.slice(0, 5)"
                    :key="collaborator.id"
                    size="24"
                    class="collaborator-avatar"
                    :class="{ 'overlap-avatar': index > 0 }"
                  >
                    <img :src="getImageUrl(collaborator.image)" alt="Collaborator's image" class="circle-image"/>
                  </v-avatar>
                  <span class="ms-2">
                    {{ project.collaborators.length }}
                    {{ project.collaborators.length === 1 ? 'collaborator' : 'collaborators' }}
                  </span>
                  <v-icon size="16" class="ms-1">mdi-chevron-down</v-icon>
                </div>
              </v-chip>
              <span v-else class="text-grey">No collaborators</span>
            </td>
            <td class="text-end actions-cell" v-if="isAdminOrProjectLeader">
              <v-tooltip text="Edit" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="action-btn edit"
                    color="primary"
                    icon
                    size="small"
                    @click.stop="openEditDialog(project)"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Delete" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="action-btn delete"
                    color="error"
                    icon
                    size="small"
                    @click.stop="deleteProject(project.id)"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
      <!-- Empty State Message -->
<div v-if="!loading && projects.length === 0" class="empty-state">
  <v-container class="text-center">
    <v-icon size="64" color="grey darken-1">mdi-emoticon-sad-outline</v-icon>
    <p class="empty-message">No projects found. Start by creating a new project! 😊</p>
  </v-container>
</div>
    </v-card>

    <!-- Create Project Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="500px" @keydown.enter="formValid && createProject()" @keydown.esc="closeDialog">
      <v-card>
        <v-card-title>
          <span class="text-h5">Create New Project</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="createProjectForm" v-model="formValid">
            <v-text-field
              v-model="newProject.name"
              label="Project Name"
              :rules="[v => !!v || 'Project name is required']"
              required
            ></v-text-field>
            <v-textarea
              v-model="newProject.description"
              label="Project Description"
              rows="4"
            ></v-textarea>

            <!-- Autocomplete for Collaborators -->
            <v-autocomplete
              v-model="newProject.collaborators"
              :items="users"
              item-title="username"
              item-value="id"
              label="Add Collaborators"
              :item-props="true"
              multiple
              chips
              outlined
            >


            </v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!formValid" @click="createProject">
            Create
          </v-btn>
          <v-btn @click="closeDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Project Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500px" @keydown.enter="formValid && updateProject()" @keydown.esc="closeDialog">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Project</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="editProjectForm" v-model="formValid">
            <v-text-field
              v-model="editedProject.name"
              label="Project Name"
              :rules="[v => !!v || 'Project name is required']"
              required
            ></v-text-field>
            <v-textarea
              v-model="editedProject.description"
              label="Project Description"
              rows="4"
            ></v-textarea>

            <!-- Autocomplete for Collaborators -->
            <v-autocomplete
              v-model="editedProject.collaborators"
              :items="users"
              :item-props="true"
              item-title="username"
              item-value="id"
              label="Add Collaborators"
              multiple
              chips
              outlined
            >
            </v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!formValid" @click="updateProject">
            Save
          </v-btn>
          <v-btn @click="closeDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Collaborators Overlay Dialog -->
    <v-dialog v-model="showCollaboratorsOverlay" max-width="640px" @keydown.esc="closeCollaboratorsOverlay">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="me-2" color="primary">mdi-account-group</v-icon>
          <span class="text-h5">Project Collaborators</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="isAdminOrProjectLeader"
            color="primary"
            variant="outlined"
            size="small"
            @click="openAddCollaboratorDialog"
          >
            <v-icon size="18" class="me-1">mdi-account-plus</v-icon>
            Add Member
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4" style="max-height: 420px; overflow-y: auto;">
          <div v-if="collaboratorsWithRoles.length > 0">
            <v-list class="py-0">
              <v-list-item
                v-for="collaborator in collaboratorsWithRoles"
                :key="collaborator.id"
                class="collaborator-item"
              >
                <template v-slot:prepend>
                  <v-avatar size="48" class="me-3">
                    <img :src="getImageUrl(collaborator.image)" alt="Collaborator's image" class="circle-image"/>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium">
                  {{ collaborator.username }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-select
                    v-if="isAdminOrProjectLeader"
                    v-model="collaborator.primaryRole"
                    :items="roleItems"
                    item-title="title"
                    item-value="name"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="role-select mt-1"
                    :loading="collaborator.roleUpdating"
                    @update:modelValue="val => updateCollaboratorRole(collaborator, val)"
                    @click.stop
                  ></v-select>
                  <v-chip v-else size="small" :color="roleColor(collaborator.primaryRole)" variant="tonal" class="mt-1">
                    {{ roleName(collaborator.primaryRole) }}
                  </v-chip>
                </v-list-item-subtitle>
                <template v-slot:append v-if="isAdminOrProjectLeader">
                  <v-btn
                    color="error"
                    variant="text"
                    size="small"
                    @click="removeCollaborator(collaborator.id)"
                  >
                    <v-icon size="18">mdi-account-minus</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </div>
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-account-group-outline</v-icon>
            <p class="text-grey mt-3">No collaborators in this project</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeCollaboratorsOverlay">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Collaborator Dialog -->
    <v-dialog v-model="showAddCollaboratorDialog" max-width="500px" @keydown.enter="newCollaborator && addCollaborator()" @keydown.esc="closeAddCollaboratorDialog">
      <v-card>
        <v-card-title>
          <span class="text-h5">Add Collaborator</span>
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="newCollaborator"
            :items="availableUsers"
            item-title="username"
            item-value="id"
            label="Select User to Add"
            :item-props="true"
            outlined
            clearable
          >
            <template v-slot:item="{ props, item }">
              <v-list-item
                v-bind="props"
                :prepend-avatar="getImageUrl(item.raw.image)"
                :title="item.raw.username"
              ></v-list-item>
            </template>
          </v-autocomplete>

          <v-divider class="my-4"></v-divider>

          <div class="text-center">
            <p class="text-caption text-grey mb-2">Or invite by email</p>
            <v-btn
              color="primary"
              variant="outlined"
              @click="openInviteDialog"
              block
            >
              <v-icon class="me-2">mdi-email-plus</v-icon>
              Invite Members by Email
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!newCollaborator" @click="addCollaborator">
            Add
          </v-btn>
          <v-btn @click="closeAddCollaboratorDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Invite Members Dialog -->
    <v-dialog v-model="showInviteDialog" max-width="600px" @keydown.enter="inviteFormValid && sendProjectInvites()" @keydown.esc="closeInviteDialog">
      <v-card>
        <v-card-title>
          <v-icon class="me-2" color="primary">mdi-email-plus</v-icon>
          <span class="text-h5">Invite Members by Email</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="inviteForm" v-model="inviteFormValid">
            <v-textarea
              v-model="inviteForm.emailsText"
              label="Email Addresses"
              placeholder="email1@example.com;email2@example.com"
              hint="Enter one or more email addresses separated by semicolon (;)"
              persistent-hint
              rows="4"
              :rules="[v => !!v || 'At least one email is required']"
              outlined
            ></v-textarea>

            <v-text-field
              v-model="inviteForm.delimiter"
              label="Delimiter"
              hint="Character used to separate emails"
              persistent-hint
              outlined
              class="mt-4"
            ></v-text-field>

            <v-select
              v-model="inviteForm.role"
              :items="roleItems"
              item-title="title"
              item-value="name"
              label="Role"
              hint="Role assigned to invited users"
              persistent-hint
              :rules="[v => !!v || 'Role is required']"
              outlined
              class="mt-4"
            ></v-select>

            <v-alert type="info" variant="tonal" class="mt-4">
              <template v-slot:prepend>
                <v-icon>mdi-information</v-icon>
              </template>
              <div class="text-caption">
                <strong>Note:</strong> Invited users will receive an email with a link to create their account.
                Once they accept, they'll automatically be added as collaborators to this project.
                If an email already exists in the system, that user will be added directly to the project.
              </div>
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!inviteFormValid"
            @click="sendProjectInvites"
          >
            <v-icon class="me-2">mdi-send</v-icon>
            Send Invites
          </v-btn>
          <v-btn @click="closeInviteDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>



<script>
import api from "@/plugins/axios";
import defaultAvatar from '@/assets/default-avatar.png';
import { toRaw } from "vue";

export default {
  data() {
    return {
      projects: [],
      selectedProjects: [],
      users: [], // Store user data for the autocomplete
      loading: true,
      search: "",
      showCreateDialog: false,
      showEditDialog: false,
      formValid: false,
      newProject: {
        name: "",
        description: "",
        collaborators: [] // Store selected collaborators' IDs
      },
      editedProject: {
        id: null,
        name: "",
        description: "",
        collaborators: [] // Store selected collaborators' IDs
      },
      baseHeaders: [
      { title: "", align: "center", value: "select", sortable: false }, // Checkbox column
      { title: "Project Name", value: "name" },
      { title: "Description", value: "description" },
      { title: "Owner", value: "owner" },
      { title: "Collaborators", value: "collaborators" }
    ],
      showCollaboratorsOverlay: false,
      selectedProjectForOverlay: null,
      collaboratorsWithRoles: [],
      showAddCollaboratorDialog: false,
      newCollaborator: null,
      showInviteDialog: false,
      inviteForm: {
        emailsText: '',
        delimiter: ';',
        role: 'transcriber'
      },
      inviteFormValid: false,
      roleItems: [
        { title: 'Transcriber', name: 'transcriber' },
        { title: 'Project Leader', name: 'project_leader' },
        { title: 'Administrator', name: 'administrator' },
      ],
    };
  },
  mounted() {
    this.fetchProjects();
    this.fetchUsers();
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.global.current.dark;
    },
    isAllSelected() {
      // true if we have items and the number of selected IDs = total items
      return (
        this.projects.length > 0 &&
        this.selectedProjects.length === this.projects.length
      );
    },
    isSomeSelected() {
      // true if there's at least one selected but not all
      return (
        this.selectedProjects.length > 0 &&
        this.selectedProjects.length < this.projects.length
      );
    },
    isAdminOrProjectLeader() {
  const userRoles = JSON.parse(localStorage.getItem("roles") || "[]"); // Parse the list of roles from localStorage
  return userRoles.includes("administrator") || userRoles.includes("project_leader");
},
  headers() {
    return this.isAdminOrProjectLeader
      ? [...this.baseHeaders, { title: "Actions", align: "end", value: "actions", sortable: false }]
      : this.baseHeaders;
  },
  availableUsers() {
    if (!this.selectedProjectForOverlay) return this.users;
    const collaboratorIds = this.selectedProjectForOverlay.collaborators.map(c => c.id);
    return this.users.filter(user => !collaboratorIds.includes(user.id));
  }
},
  methods: {
    toggleSelectAll() {
  if (this.isAllSelected) {
    // If all are selected, clicking again unselects everything
    this.selectedProjects = [];
  } else if (this.isSomeSelected) {
    // If partially selected (indeterminate),
    // we can choose to unselect everything here as well:
    this.selectedProjects = [];
  } else {
    // Otherwise, none are selected, so let's select all
    this.selectedProjects = this.projects.map((p) => p.id);
  }
},
    handleEdit() {
  if (this.selectedProjects.length === 1) {
    const projectId = this.selectedProjects[0];
    const project = this.projects.find((proj) => proj.id === projectId);
    this.openEditDialog(project);
  }
},
handleDelete() {
  if (this.selectedProjects.length > 0) {
    if (confirm("Are you sure you want to delete the selected projects?")) {
      this.selectedProjects.forEach((projectId) => {
        this.deleteProject(projectId);
      });
      this.selectedProjects = [];
    }
  }
},

    getImageUrl(imagePath) {
      if (!imagePath) return defaultAvatar;
      if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
      const baseURL = api.defaults.baseURL || "http://localhost:8000";
      return `${baseURL}${imagePath}`;
    },
    getImageUrlAutoComplete(imagePath) {
      const baseURL = api.defaults.baseURL || "http://localhost:8000"; // Use Axios base URL
      // console.log(imagePath); // Verify the image path
      // console.log("Base URL:", baseURL); // Verify the base URL
      // console.log("Resolved URL:", imagePath ? `${baseURL}${imagePath}` : "/default-avatar.png");
      return imagePath ? `${imagePath}` :defaultAvatar; // Default avatar if null or missing
    },
    async fetchProjects() {
      try {
        const response = await api.get("/api/projects/");
        this.projects = response.data;
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers() {
      try {
        const response = await api.get("/api/users/usernames-images/");
        this.users = response.data.map((user) => ({
          id: user.id,
          username: user.username,
          image: user.image,
        }));
        //this.users = this.users.map((user) => ({ ...toRaw(user) }));
        console.log("Users:", this.users); // Verify the structure
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    goToHome() {
      this.$router.push("/");
    },
    openCreateDialog() {
      //this.fetchUsers(); // Fetch users when opening the dialog
      console.log("Users:", this.users); // Verify the structure
      this.showCreateDialog = true;
      this.newProject = { name: "", description: "", collaborators: [] };
    },
    openEditDialog(project) {
  //this.fetchUsers(); // Fetch users when opening the dialog
  this.showEditDialog = true;
  this.editedProject = {
    ...project,
    collaborators: project.collaborators.map((collaborator) => ({
      id: collaborator.id,
      username: collaborator.username,
      image: collaborator.image,
    })),
  };
},

    async createProject() {
      try {
        const response = await api.post("/api/projects/", this.newProject);
        this.projects.push(response.data);
        this.closeDialog();
      } catch (error) {
        console.error("Error creating project:", error);
      }
    },
    async updateProject() {
      try {
        console.log("Edited Project:", this.editedProject);

    // Transform collaborators into an array of IDs
    const collaboratorsIds = this.editedProject.collaborators.map(collaborator =>
      typeof collaborator === "object" ? collaborator.id : collaborator
    );

        const response = await api.patch(`/api/projects/${this.editedProject.id}/`, {
          name: this.editedProject.name,
          description: this.editedProject.description,
          collaborators: collaboratorsIds
        });
        const index = this.projects.findIndex((project) => project.id === response.data.id);
        if (index !== -1) this.projects.splice(index, 1, response.data);
        this.closeDialog();
      } catch (error) {
        console.error("Error updating project:", error);
      }
    },
    async deleteProject(projectId) {
      if (confirm("Are you sure you want to delete this project?")) {
        try {
          await api.delete(`/api/projects/${projectId}/`);
          this.projects = this.projects.filter((p) => p.id !== projectId);
        } catch (error) {
          console.error("Error deleting project:", error);
        }
      }
    },
    goToProject(projectId) {
      this.$router.push(`/projects/${projectId}`);
    },
    closeDialog() {
      this.showCreateDialog = false;
      this.showEditDialog = false;
      this.newProject = { name: "", description: "", collaborators: [] };
      this.editedProject = { id: null, name: "", description: "", collaborators: [] };
    },
    roleColor(role) {
      const map = { administrator: 'error', project_leader: 'primary', transcriber: 'success' };
      return map[role] || 'grey';
    },
    roleName(role) {
      const item = this.roleItems.find(r => r.name === role);
      return item ? item.title : (role || 'No role');
    },
    async openCollaboratorsOverlay(project) {
      this.selectedProjectForOverlay = project;
      this.showCollaboratorsOverlay = true;
      await this.fetchCollaboratorsWithRoles(project.id);
    },
    async fetchCollaboratorsWithRoles(projectId) {
      try {
        const response = await api.get(`/api/projects/${projectId}/collaborators/`);
        this.collaboratorsWithRoles = response.data.map(c => ({
          ...c,
          primaryRole: (c.roles && c.roles[0]) || 'transcriber',
          roleUpdating: false,
        }));
      } catch (error) {
        console.error("Error fetching collaborators:", error);
      }
    },
    async updateCollaboratorRole(collaborator, newRole) {
      if (!this.selectedProjectForOverlay) return;
      collaborator.roleUpdating = true;
      try {
        await api.patch(`/api/projects/${this.selectedProjectForOverlay.id}/update-member-role/`, {
          user_id: collaborator.id,
          role: newRole,
        });
      } catch (error) {
        console.error("Error updating role:", error);
        // revert on error
        collaborator.primaryRole = (collaborator.roles && collaborator.roles[0]) || 'transcriber';
      } finally {
        collaborator.roleUpdating = false;
      }
    },
    closeCollaboratorsOverlay() {
      this.showCollaboratorsOverlay = false;
      this.selectedProjectForOverlay = null;
      this.collaboratorsWithRoles = [];
    },
    openAddCollaboratorDialog() {
      this.showAddCollaboratorDialog = true;
      this.newCollaborator = null;
    },
    closeAddCollaboratorDialog() {
      this.showAddCollaboratorDialog = false;
      this.newCollaborator = null;
    },
    openInviteDialog() {
      this.showInviteDialog = true;
      this.inviteForm = {
        emailsText: '',
        delimiter: ';',
        role: 'transcriber'
      };
    },
    closeInviteDialog() {
      this.showInviteDialog = false;
      this.inviteForm = {
        emailsText: '',
        delimiter: ';',
        role: 'transcriber'
      };
    },
    async sendProjectInvites() {
      if (!this.selectedProjectForOverlay) return;

      try {
        const response = await api.post(
          `/api/projects/${this.selectedProjectForOverlay.id}/invite-members/`,
          {
            emails_text: this.inviteForm.emailsText,
            delimiter: this.inviteForm.delimiter,
            role: this.inviteForm.role
          }
        );

        // Show success message
        const created = response.data.created || [];
        const updated = response.data.updated || [];
        const errors = response.data.errors || [];

        let message = response.data.success + '\n';
        if (created.length > 0) {
          message += `\nInvitations sent/Users added: ${created.length}`;
        }
        if (updated.length > 0) {
          message += `\nInvitations updated: ${updated.length}`;
        }
        if (errors.length > 0) {
          message += `\nErrors: ${errors.join(', ')}`;
        }

        alert(message);

        // Refresh the project to show any newly added existing users
        await this.fetchProjects();

        // Update the overlay if it's still open
        const updatedProject = this.projects.find(p => p.id === this.selectedProjectForOverlay.id);
        if (updatedProject) {
          this.selectedProjectForOverlay = updatedProject;
        }

        this.closeInviteDialog();
        this.closeAddCollaboratorDialog();

        // Refresh collaborators overlay if it's still open
        if (this.showCollaboratorsOverlay && this.selectedProjectForOverlay) {
          await this.fetchCollaboratorsWithRoles(this.selectedProjectForOverlay.id);
        }
      } catch (error) {
        console.error("Error sending invites:", error);
        alert("Error sending invitations: " + (error.response?.data?.error || error.message));
      }
    },
    async addCollaborator() {
      if (!this.newCollaborator || !this.selectedProjectForOverlay) return;

      try {
        const currentCollaborators = this.selectedProjectForOverlay.collaborators.map(c => c.id);
        const updatedCollaborators = [...currentCollaborators, this.newCollaborator];

        const response = await api.patch(`/api/projects/${this.selectedProjectForOverlay.id}/`, {
          collaborators: updatedCollaborators
        });

        // Update the project in the list
        const projectIndex = this.projects.findIndex(p => p.id === this.selectedProjectForOverlay.id);
        if (projectIndex !== -1) {
          this.projects[projectIndex] = response.data;
          this.selectedProjectForOverlay = response.data;
        }

        this.closeAddCollaboratorDialog();
        await this.fetchCollaboratorsWithRoles(this.selectedProjectForOverlay.id);
      } catch (error) {
        console.error("Error adding collaborator:", error);
      }
    },
    async removeCollaborator(collaboratorId) {
      if (!this.selectedProjectForOverlay) return;

      if (confirm("Are you sure you want to remove this collaborator?")) {
        try {
          const updatedCollaborators = this.selectedProjectForOverlay.collaborators
            .map(c => c.id)
            .filter(id => id !== collaboratorId);

          const response = await api.patch(`/api/projects/${this.selectedProjectForOverlay.id}/`, {
            collaborators: updatedCollaborators
          });

          // Update the project in the list
          const projectIndex = this.projects.findIndex(p => p.id === this.selectedProjectForOverlay.id);
          if (projectIndex !== -1) {
            this.projects[projectIndex] = response.data;
            this.selectedProjectForOverlay = response.data;
          }
          await this.fetchCollaboratorsWithRoles(this.selectedProjectForOverlay.id);
        } catch (error) {
          console.error("Error removing collaborator:", error);
        }
      }
    },
  }
};
</script>



<style scoped>
.back-btn {
  margin-left: 10px;
}

.toolbar-btn {
  margin-right: 10px;
}

.v-data-table {
  border-radius: 12px;
  overflow: hidden;
}

.v-data-table th {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 14px;
}

.v-btn {
  margin: 0 4px;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.clickable-row:hover {
  background-color: rgba(var(--v-border-color), 0.08);
}

/* New square action buttons */
.actions-cell {
  white-space: nowrap;
}
.action-btn {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  border-radius: 6px !important; /* square with slight rounding */
  box-shadow: none !important;
  transition: background-color 0.2s ease, transform 0.15s ease;
  margin: 0 2px;
}
.action-btn.edit:hover { background-color: #1976d2 !important; }
.action-btn.delete:hover { background-color: #d32f2f !important; }
.action-btn:active { transform: scale(0.94); }
.v-data-table >>> th {
  background-color: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface));
  font-weight: bold !important;
  font-size: 14px;
}
.v-avatar {
  border: 2px solid rgb(var(--v-theme-surface));
  overflow: hidden;
}

.me-1 {
  margin-right: 8px; /* Space between collaborator images */
}

.me-2 {
  margin-right: 16px; /* Space between owner image and username */
}
.circle-image {
  object-fit: cover; /* Ensures the image covers the entire circle */
  border-radius: 50%; /* Makes the image circular */
  width: 100%; /* Fills the parent container */
  height: 100%; /* Fills the parent container */
  display: block; /* Ensures the image behaves as a block element */
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px; /* Adjust as necessary */
  color: var(--v-theme-on-background); /* Automatically handles light/dark themes */
  opacity: 0.7; /* Subtle faded effect */
}

.empty-message {
  font-size: 1.2rem;
  margin-top: 16px;
  color: inherit;
}

/* Collaborators chip styling */
.collaborators-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.collaborators-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.collaborator-avatar {
  border: 2px solid rgb(var(--v-theme-surface));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.overlap-avatar {
  margin-left: -8px;
}

/* Collaborator overlay styling */
.collaborator-item {
  border-radius: 12px;
  margin-bottom: 8px;
  background-color: rgba(var(--v-border-color), 0.04);
  transition: background-color 0.2s ease;
}

.collaborator-item:hover {
  background-color: rgba(var(--v-border-color), 0.1);
}

.role-select {
  max-width: 180px;
}


</style>
